import type { Collection } from '../collection';
import { debugPerformance } from '../utils';
import { createFinalizationRegistry } from './gc';
import { hashCollection } from './hashCollection';
import { IS_WEAK_HASHING_SUPPORTED } from './support';

/**
 * This module is an experimental approach for caching collections
 * and create unique references for a set of values.
 *
 * It's currently not used as it decreases performance. But we might want to use it in the future
 * as it can improve memory usage.
 */

/**
 * Mapping of class constructor to a map of hash code to their WeakRef .
 */
const HASH_TO_COLLECTION = new WeakMap<
    (typeof Object)['constructor'],
    Map<number, Array<WeakRef<Readonly<Collection>>>>
>();

/**
 * Freeze a collection and return an existant frozen collection if it exists.
 */
export function freezeCollection<T extends Collection>(collection: T, forceFreezing = false): T {
    // TODO: apply in some cases
    if (!forceFreezing || !IS_WEAK_HASHING_SUPPORTED) {
        return collection;
    }

    debugPerformance.freezeCollectionsCount++;
    const hashCode = hashCollection(collection);
    const existing = getCollectionsByHash<T>(collection, hashCode);

    if (existing) {
        debugPerformance.freezeCollectionsCachedCount++;
        return existing;
    }

    return cacheCollectionByHash(hashCode, collection);
}

/**
 * Get a collection by its hash code.
 * Hashes can collide, meaning that a collection can have the same hash as another one.
 * In this case, we need to check if the collection is equal to the existing one.
 */
function getCollectionsByHash<T extends Collection>(
    collection: T,
    hashCode: number
): T | undefined {
    const map = HASH_TO_COLLECTION.get(collection.constructor);
    if (!map) {
        return;
    }

    const refs = map.get(hashCode);
    if (!refs) {
        return;
    }

    for (const ref of refs) {
        const deref = ref.deref();
        if (deref) {
            if (deref.equals(collection)) {
                // @ts-ignore
                return deref;
            }
            debugPerformance.hashCollisionsCount++;
        } else {
            // Remove from the array already as the value is no longer referenced.
            refs.splice(refs.indexOf(ref), 1);
        }
    }

    return;
}

// Prevent cache to grow too much by keeping it in sync with garbage collection.
const registry = createFinalizationRegistry<{
    typeConstructor: (typeof Object)['constructor'];
    hashCode: number;
}>(({ typeConstructor, hashCode }) => {
    HASH_TO_COLLECTION.get(typeConstructor)?.delete(hashCode);
});

/**
 * Cache an object by its hash code.
 */
function cacheCollectionByHash<T extends Collection>(hashCode: number, collection: T): T {
    let map = HASH_TO_COLLECTION.get(collection.constructor);
    if (!map) {
        map = new Map();
        HASH_TO_COLLECTION.set(collection.constructor, map);
    }

    const ref = new WeakRef(collection);

    let existing = map.get(hashCode);
    if (!existing) {
        existing = [];
        map.set(hashCode, existing);
    }
    existing.push(ref);

    registry?.register(collection, { typeConstructor: collection.constructor, hashCode });

    return collection;
}
